// src/modules/assinaturas/estabelecimento/utils/periodosUtils.ts
import React from 'react';

export type Periodo = {
    _id: string;
    inicio?: string;
    fim?: string;
    ativo?: boolean;

    servicos?: Array<{ serviceId?: string; serviceName?: string; limit?: number; used?: number }>;
    usos?: Array<{ serviceId?: string; nomeServico?: string; limite?: number; quantidadeUsada?: number }>;

    valorPrevisto?: number;
    valorPago?: number;
    cobrancaStatus?: 'pendente' | 'parcial' | 'pago' | 'vencido' | 'cancelado' | 'estornado' | string;
    dueAt?: string;
    paidAt?: string;
    invoiceId?: string;

    [key: string]: any;
};

export type PlanoService = {
    serviceId: string;
    serviceName?: string;
    recurrence: 'semanal' | 'mensal' | 'total';
    quantity: number;
};

export type EditState = {
    open: boolean;
    periodo?: Periodo | null;
    valorPago?: number | null;
    cobrancaStatus?: Periodo['cobrancaStatus'];
    paidAt?: string | null;
    paymentMethod?: string;
};

export const unitLabel: Record<string, string> = {
    dia: 'dia(s)',
    semana: 'semana(s)',
    mes: 'mês(es)',
    ano: 'ano(s)',
};

export const formatDateBR = (value?: string | Date | null) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('pt-BR');
};

/** Dia civil gravado como meia-noite UTC (inicio, fim, vencimento, cobranca): exibe o dia sem deslocar pelo fuso. */
export const formatCivilDateBR = (value?: string | Date | null) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

export const formatCurrencyBRL = (value?: number | null) =>
    Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const BILLING_STATUS: Record<string, { label: string; tone: string }> = {
    pago: { label: 'Pago', tone: 'green' },
    parcial: { label: 'Parcial', tone: 'orange' },
    vencido: { label: 'Vencido', tone: 'red' },
    cancelado: { label: 'Cancelado', tone: 'gray' },
    estornado: { label: 'Estornado', tone: 'gray' },
    pendente: { label: 'Pendente', tone: 'blue' },
};

export const BillingStatusTag = ({ status }: { status?: string }) => {
    const { label, tone } = BILLING_STATUS[(status ?? 'pendente').toString().toLowerCase()] || BILLING_STATUS.pendente;

    return (
        <span className="sf-tag" data-tone={tone}>
            {label}
        </span>
    );
};

export const totalUsed = (row: Periodo) => {
    if (Array.isArray(row.servicos) && row.servicos.length) {
        return row.servicos.reduce((acc, s) => acc + (s.used ?? 0), 0);
    }
    if (Array.isArray(row.usos) && row.usos.length) {
        return row.usos.reduce((acc, u) => acc + (u.quantidadeUsada ?? 0), 0);
    }
    return 0;
};

export const canEditPeriodo = (isAssinaturaAtiva: boolean, status?: string) => {
    if (!isAssinaturaAtiva) return false;
    const s = (status || '').toString().toLowerCase();
    return s !== 'pago' && s !== 'cancelado';
};

export const cantEditReason = (isAssinaturaAtiva: boolean, status?: string) => {
    if (!isAssinaturaAtiva) return 'Assinatura cancelada';
    const s = (status || '').toString().toLowerCase();
    if (s === 'pago') return 'Período já pago';
    if (s === 'cancelado') return 'Período cancelado';
    return 'Edição indisponível';
};

export const resolveServiceLabel = (
    serviceId: string | undefined,
    explicitName: string | undefined,
    planServicesMap: Map<string, { serviceName?: string; quantity?: number; recurrence?: string }>,
    servicesDict: Record<string, string>
): string => {
    if (!serviceId) return explicitName || '-';
    return explicitName || planServicesMap.get(serviceId)?.serviceName || servicesDict[serviceId] || serviceId;
};
